import React, { Fragment, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useSelector, useDispatch } from 'react-redux';

import { clearValidationErrorsOnChange } from '../../../../hocs/useFormWithValidation';
import { PROFILE_FIELDS_TYPES } from '../../../../constants/formFields';
import { getFieldProps } from '../../../../utils/fields/getFieldProps';
import { useCountrySelect } from '../../../../hocs/useCountrySelect';
import { HomeAddressFormSection } from '../../../ProfileLayout/HomeAddress/HomeAddressFormSection';
import { makeInitialValue } from '../../../ProfileLayout/HomeAddress/utils';
import { FormErrorBlock } from '../../../common/FormErrorBlock';
import DateDropdown from '../../../common/DateDropdown';
import SelectCustom from '../../../SelectCustom';
import TextInput from '../../../TextInput';
import { IDS } from '../../../../constants/ids';
import Button from '../../../Button';
import { usePatientCountryDetails } from '../usePatientCountryDetails';
import { usePatientForm } from './usePatientForm';
import useGetIsFieldDisabled from '../../../../hooks/patient-form/useGetIsFieldDisabled';
import { INLINE_TYPES } from '../../../../constants';
import { enhance } from './enhance';
import { selectLanguageTypes } from '../../../../redux/data/selectors';
import { appointmentProgressActions } from '../../../../redux/appointmentForm/progress-types';

const {
  LAST_NAME,
  FIRST_NAME,
  DOB,
  GENDER,
  HOME_PHONE,
  RACE,
  ETHNICITY,
  LANGUAGE,
} = PROFILE_FIELDS_TYPES;

function PatientForm({
  onSubmit,
  errors,
  genderList,
  raceList,
  ethnicityList,
  isEirVisitType,
  countryList,
  formatMessage,
  patientForm,
  details,
  getCountryDetails,
  visitTypeCode,
}) {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const dispatch = useDispatch();

  const languageList = useSelector(selectLanguageTypes);

  const targetCountryEntity = usePatientCountryDetails();

  const language = get(details, 'language', null);

  const isInlineVisitType = INLINE_TYPES.includes(visitTypeCode);

  useEffect(() => {
    if (!isInlineVisitType && targetCountryEntity)
      getCountryDetails({ code: targetCountryEntity.value });
  }, [targetCountryEntity, isInlineVisitType]);

  const { checkIsFieldDisabled } = useGetIsFieldDisabled();

  const {
    stateList,
    zipLimit,
    isZipApplicable,
    zipExamples,
  } = useCountrySelect(formatMessage);

  const {
    handleFormChange,
    validateErrors,
    clearErrors,
    isDisabled,
    setCountryValue,
    formState,
    isValid,
  } = usePatientForm(
    errors,
    details,
    patientForm,
    isZipApplicable,
    isInlineVisitType,
    isEirVisitType,
    makeInitialValue(details, countryList, stateList, isEirVisitType, true),
  );

  // immediately show errors for postal code field, but for other fields
  // show errors only after form submission attempt
  const formErrors = isFormSubmitted ? validateErrors : {};

  const isPhoneNumberFieldDisabled =
    isDisabled(HOME_PHONE) && checkIsFieldDisabled(HOME_PHONE, validateErrors);

  const onCountryChange = data => {
    getCountryDetails({ code: data.value });
    setCountryValue(data);
  };

  const onLanguageChange = data => {
    handleFormChange({ [LANGUAGE]: data.value });
    dispatch(appointmentProgressActions.setPreferredLanguage(data.value));
  };

  const makeField = getFieldProps(formatMessage, handleFormChange);

  clearValidationErrorsOnChange(clearErrors, formState);

  const isHomePhoneValid = formState[HOME_PHONE]
    ? formState[HOME_PHONE].length >= 8
    : true;

  const handleSubmit = e => {
    e.preventDefault();
    clearErrors();

    const fullCountryName = get(formState, 'country.label', '');
    const fullStateName = get(formState, 'state.label', '');

    const payload = {
      ...omit(formState, ['language', 'race', 'ethnicity']),
      ...(formState.race && { race: formState.race }),
      ...(formState.ethnicity && { ethnicity: formState.ethnicity }),
      ...(formState.language && { language: formState.language }),
      country: fullCountryName,
      state: fullStateName,
    };

    setIsFormSubmitted(true);

    onSubmit(payload);
  };

  useEffect(() => {
    if (language) {
      dispatch(appointmentProgressActions.setPreferredLanguage(language));
    }
  }, [language]);

  return (
    <form onSubmit={handleSubmit}>
      {/*     F I R S T   N A M E     */}
      <TextInput
        {...makeField(FIRST_NAME)}
        value={formState.firstName}
        disabled={isDisabled(FIRST_NAME)}
      />
      <FormErrorBlock type={FIRST_NAME} errors={validateErrors} />
      {/*     L A S T   N A M E     */}
      <TextInput
        {...makeField(LAST_NAME)}
        value={formState.lastName}
        disabled={isDisabled(LAST_NAME)}
      />
      <FormErrorBlock type={LAST_NAME} errors={validateErrors} />
      {/*     G E N D E R     */}
      {!isInlineVisitType && (
        <SelectCustom
          {...makeField(GENDER)}
          options={genderList}
          value={formState.gender}
          disabled={isDisabled(GENDER)}
        />
      )}
      {/*     D O B     */}
      <DateDropdown
        {...makeField(DOB)}
        value={formState.dob}
        disabled={isDisabled(DOB)}
      />

      {isEirVisitType && (
        <Fragment>
          <SelectCustom
            {...makeField(RACE)}
            value={formState.race}
            options={raceList}
            required
            disabled={isDisabled(RACE)}
          />
          <SelectCustom
            {...makeField(ETHNICITY)}
            value={formState.ethnicity}
            options={ethnicityList}
            required
            disabled={isDisabled(ETHNICITY)}
          />

          <SelectCustom
            className="mt3"
            onChange={onLanguageChange}
            name={LANGUAGE}
            type="select"
            label={formatMessage({ id: 'profile.fields.prefLanguage' })}
            placeholder={formatMessage({ id: 'profile.fields.prefLanguage' })}
            value={formState.language}
            options={languageList}
            required
            disabled={isDisabled(LANGUAGE)}
          />
        </Fragment>
      )}
      {isInlineVisitType && (
        <Fragment>
          <TextInput
            {...makeField(HOME_PHONE)}
            required
            value={formState.homePhone}
            disabled={isPhoneNumberFieldDisabled}
          />

          <FormErrorBlock type={HOME_PHONE} errors={formErrors} />
        </Fragment>
      )}
      {!isInlineVisitType && (
        <HomeAddressFormSection
          {...formState}
          formatMessage={formatMessage}
          countryList={countryList}
          validateErrors={validateErrors}
          targetCountryEntity={targetCountryEntity}
          isZipApplicable={isZipApplicable}
          onChange={handleFormChange}
          onCountryChange={onCountryChange}
          statesList={stateList}
          zipLimit={zipLimit}
          isDisabled={isDisabled}
          zipExamples={zipExamples}
          homePhoneRequired
          cityRequired
          address1Required
          rowMode
          isAppointmentPatientForm
          isFormSubmitted={isFormSubmitted}
        />
      )}
      <div className="mt4 tr">
        <Button
          id={IDS.APPOINTMENT_FORM.SUBMIT_PATIENT_FORM}
          type="submit"
          raised
          color="primary"
          disabled={!isValid || !isHomePhoneValid}
        >
          {formatMessage({ id: 'common.next' })}
        </Button>
      </div>
    </form>
  );
}

PatientForm.propTypes = {
  formatMessage: PropTypes.func,
  onSubmit: PropTypes.func,
  genderList: PropTypes.array,
  raceList: PropTypes.array,
  ethnicityList: PropTypes.array,
  isEirVisitType: PropTypes.bool,
  countryList: PropTypes.array,
  details: PropTypes.object,
  patientForm: PropTypes.object,
  errors: PropTypes.array,
  getCountryDetails: PropTypes.func,
  visitTypeCode: PropTypes.string,
};

export default enhance(PatientForm);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentPatientInfo/PatientForm/index.jsx